import { SlotObserver } from './slot-observer';
import UrlChangeWatcher from './url-change-watcher';
import assertTargetGetElem from './utils/assert-target-get-element';
import airbrake from './airbrake';
import { CampaignRenderer } from './campaign-renderer';
import { getMetadata } from './utils/analytics';
import { getCheckCampaignsWithRetry, ackCampaign } from './api-client';

const RETRY_TIMES = 19;

export class MarketingLoaderEngine {
  constructor ({
    doNotTrack = false,
    slotObserver,
    urlChangeWatcher,
    campaignRenderer,
    checkCampaigns
  }) {
    this.doNotTrack = doNotTrack;
    this.slotObserver = slotObserver || new SlotObserver({ onSlotsMount: (slots) => this.mountSlots(slots), onSlotsUnMount: (slots) => this.unMountSlots(slots) });
    this.urlChangeWatcher = urlChangeWatcher || new UrlChangeWatcher({ onUrlChange: this.onUrlChange.bind(this) });
    this.campaignRenderer = campaignRenderer || new CampaignRenderer();
    this.checkCampaigns = checkCampaigns || getCheckCampaignsWithRetry({times: RETRY_TIMES});
  }

  start () {
    this.slotObserver.startObserving();
    this.urlChangeWatcher.startObserving();
  }

  stop () {
    this.slotObserver.stopObserving();
    this.urlChangeWatcher.stopObserving();
  }

  async unMountSlots (slots) {
    const slotElements = slots.map(slot => assertTargetGetElem(slot.node));
    slotElements.forEach((slotElement) => this.campaignRenderer.unRenderCampaign(slotElement));
  }

  async mountSlots (slots = []) {
    if (slots.length === 0) return;
    try {
      const slotElements = slots
        .reduce((obj, slot) => {
          obj[slot.slotName] = assertTargetGetElem(slot.node);
          return obj;
        }, {});

      const slotsData = await this.checkCampaigns({ ...getMetadata(), slotNames: Object.keys(slotElements) });

      if (!Array.isArray(slotsData.result)) throw new Error('Checking campaign: malformed response');

      for (let i = 0; i < slotsData.result.length; i++) {
        const {
          reactComponentName,
          renderProps,
          id: campaignId,
          htmlContent,
          slotName
        } = slotsData.result[i];
        const slotElement = slotElements[slotName];
        if (!slotElement) throw new Error(`Checking campaign: error selecting the slot ${slotName}`);

        if (!campaignId) {
          // no relevant campaign
          continue;
        }
        this.campaignRenderer.renderCampaign({
          slotElement,
          slotProps: slotsData.result[i],
          reactComponentName,
          renderProps,
          campaignId,
          htmlContent: htmlContent ? decodeURIComponent(htmlContent) : null
        });
      }
      if (window.performance && window.performance.mark) {
        window.performance.mark('tes-module-marketing-first-render');
      }
    } catch (error) {
      console.log(error);
      airbrake.notify(error);
    }
  }

  async ackSlot (campaignId) {
    try {
      await ackCampaign(campaignId);
    } catch (error) {
      console.log(error);
      airbrake.notify(error);
    }
  }

  // used for debugging
  renderCampaign (targetIdOrElem, reactComponentName, renderProps) {
    const slotElement = assertTargetGetElem(targetIdOrElem);
    this.campaignRenderer.renderCampaign({
      slotElement,
      reactComponentName,
      renderProps
    });
  }

  async onUrlChange () {
    await this.slotObserver.findAndUnMountSlots([document.body]);
    await this.slotObserver.findAndMountSlots([document.body]);
  }
}

let engineInstance;

const init = (debug) => {
  engineInstance = new MarketingLoaderEngine({ doNotTrack: debug });
  return engineInstance;
};

export default init;
